import { SearchOutlined } from "@ant-design/icons";
import { Empty, Input, Table, TableProps } from "antd";
import { ColumnGroupType, ColumnType } from "antd/es/table";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useContext, useEffect, useMemo, useState } from "react";

import { DEFAULT_DAYS, MIN_TABLE_WIDTH, tableClass, tableCompact, tableDefault } from "./constants";
import { DashboardCardExtraContext } from "./DashboardCardExtraContext";

import "./SearchableTable.css";

function useClientSearch<T>(rows: T[], keys: Array<(r: T) => unknown>) {
    const [q, setQ] = useState("");
    const ql = q.trim().toLowerCase();

    const data = useMemo(() => {
        if (!ql) return rows;
        return rows.filter((r) => keys.map((fn) => String(fn(r) ?? "").toLowerCase()).some((v) => v.includes(ql)));
    }, [rows, ql, keys]);

    return { q, setQ, data };
}

export const SearchableTable = <T extends object>({
    rows,
    columns,
    loading,
    rowKey,
    searchKeys,
    searchPlaceholder,
    maxRows = DEFAULT_DAYS,
    truncate = true,
    compact,
    allowSearch = true,
    allowSuffix = true,
    scroll,
    ...tableProps
}: {
    rows: T[];
    columns: (ColumnGroupType<T> | ColumnType<T>)[];
    loading?: boolean;
    rowKey: (r: T) => string;
    searchKeys: Array<(r: T) => unknown>;
    searchPlaceholder?: string;
    maxRows?: number;
    truncate?: boolean;
    compact?: boolean;
    allowSearch?: boolean;
    allowSuffix?: boolean;
} & Omit<TableProps<T>, "dataSource" | "columns" | "loading" | "rowKey" | "pagination">) => {
    const { t } = usePaldockTranslation("common");
    const { q, setQ, data } = useClientSearch(rows, searchKeys);
    const extraCtx = useContext(DashboardCardExtraContext);

    const searchEl = useMemo(
        () => (
            <Input
                allowClear
                placeholder={searchPlaceholder ?? t("search")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                prefix={<SearchOutlined className="text-neutral-400 text-xs" />}
                className={[
                    "!w-[156px]",
                    "!h-[32px]",
                    "[&_.ant-input-affix-wrapper]:!rounded-md",
                    "[&_.ant-input-affix-wrapper]:!border-neutral-200",
                    "[&_.ant-input-affix-wrapper]:flex [&_.ant-input-affix-wrapper]:items-center",
                    "[&_.ant-input-affix-wrapper-focused]:!ring-1",
                    "[&_.ant-input-affix-wrapper-focused]:!ring-[#22C55E]",
                    "[&_.ant-input]:!leading-tight [&_.ant-input]:px-1",
                ].join(" ")}
            />
        ),
        [q, searchPlaceholder, t]
    );

    useEffect(() => {
        if (!extraCtx || !allowSearch) return;
        extraCtx.setExtra(searchEl);
        return () => extraCtx.setExtra(null);
    }, [searchEl]);

    useEffect(() => {
        if (!allowSuffix) {
            return;
        }
        extraCtx?.setTitleSuffix?.(<>({data.length})</>);
        return () => extraCtx?.setTitleSuffix?.(null);
    }, [data.length]);

    const dataCapped = maxRows ? data.slice(0, maxRows) : data;

    const cols = useMemo(
        () =>
            columns.map((c: any) => ({
                ...c,
                ellipsis: truncate ? true : c.ellipsis,
                fixed: c.key === "actions" ? "right" : c.fixed,
                onCell: () => ({
                    style: truncate
                        ? { whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", maxWidth: 0 }
                        : undefined,
                }),
            })),
        [columns, truncate]
    );

    return (
        <div className={compact ? tableCompact : tableDefault}>
            {!extraCtx && allowSearch && <div className="mb-2 flex justify-end">{searchEl}</div>}
            <Table
                className={[truncate ? "dash-table--truncate" : "", tableClass].join(" ")}
                size="small"
                pagination={false}
                loading={loading}
                rowKey={rowKey}
                dataSource={dataCapped}
                columns={cols as any}
                locale={{ emptyText: <Empty description={t("no_data")} /> }}
                style={{ borderRadius: 10 }}
                {...tableProps}
                scroll={{ ...scroll, x: MIN_TABLE_WIDTH }}
            />
        </div>
    );
};
